import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../services/api';
import { useSession } from '../store/session';

interface LabSummary {
  id: string;
  slug: string;
  title: string;
  version: number;
  status: string;
  accessTier: 'free' | 'premium';
  subject: string;
  gradeBand: string;
  outcomes: string[];
}

/**
 * The laboratory shelf.
 *
 * The outline lists 22 laboratories. One of them exists, and the honest thing
 * to do with the other 21 is say so — a grid of greyed-out tiles that never
 * open is how a demo loses a ministry's confidence in the ten seconds after the
 * third click.
 */
const PLANNED = [
  'Physics', 'Chemistry', 'Biology', 'Integrated Science', 'Agriculture',
  'Home Science', 'Health Science', 'Computer Science', 'Environmental Science',
  'Drawing and Design', 'Aviation Technology', 'Building and Construction',
  'Electrical Technology', 'Metal Technology', 'Power Mechanics',
  'Wood Technology', 'Media Technology', 'Marine Technology', 'Engineering',
  'Coding', 'Robotics', 'Artificial Intelligence',
];

export function LabsPage() {
  const { orgSlug = '' } = useParams();
  const canAuthor = useSession(
    state => state.permissions.includes('lab@update') || state.permissions.includes('*@*'),
  );

  const { data, isLoading } = useQuery({
    queryKey: ['labs'],
    queryFn: () => apiClient.get<LabSummary[]>('/v1/labs'),
  });

  const published = (data ?? []).filter(lab => lab.status === 'published');
  const drafts = (data ?? []).filter(lab => lab.status !== 'published');

  return (
    <div>
      <header className="mb-5">
        <h1 className="text-2xl font-bold text-ink-900">Virtual STEM laboratories</h1>
        <p className="text-sm text-ink-600">
          Interactive practical work that runs on any device, down to a feature phone on 2G — and
          asks exactly the same questions at every level of detail.
        </p>
      </header>

      {isLoading && <p className="text-sm text-ink-500">Loading…</p>}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {published.map(lab => (
          <Link
            key={lab.id}
            to={`/o/${orgSlug}/labs/${lab.slug}`}
            className="card flex flex-col gap-2 transition-shadow hover:shadow-md"
          >
            <div className="flex items-start justify-between gap-2">
              <span className="text-xs font-medium uppercase tracking-wide text-brand-700">
                {lab.subject}
              </span>
              <span className={lab.accessTier === 'premium' ? 'badge-premium' : 'badge-free'}>
                {lab.accessTier}
              </span>
            </div>
            <h2 className="text-base font-semibold text-ink-900">{lab.title}</h2>
            <p className="text-xs text-ink-500">
              {lab.gradeBand} · version {lab.version}
            </p>
            {lab.outcomes.length > 0 && (
              <p className="mt-auto font-mono text-[0.7rem] text-ink-500">
                {lab.outcomes.join(' · ')}
              </p>
            )}
          </Link>
        ))}
      </div>

      {canAuthor && drafts.length > 0 && (
        <section className="mt-8">
          <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-ink-500">
            Drafts
          </h2>
          <ul className="space-y-2">
            {drafts.map(lab => (
              <li key={lab.id}>
                <Link
                  to={`/o/${orgSlug}/labs/${lab.slug}/preview`}
                  className="card flex items-center justify-between gap-3 text-sm hover:border-brand-400"
                >
                  <span>{lab.title}</span>
                  <span className="text-xs text-ink-500">Preview →</span>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="mt-10">
        <h2 className="mb-1 text-sm font-semibold uppercase tracking-wide text-ink-500">
          In development
        </h2>
        <p className="mb-3 text-sm text-ink-600">
          The engine is one interpreter over a declarative lab script, so each of these is authoring
          work rather than a new application.
        </p>
        <div className="flex flex-wrap gap-2">
          {PLANNED.map(subject => (
            <span
              key={subject}
              className="rounded-full bg-white px-3 py-1 text-xs text-ink-500 ring-1 ring-ink-200"
            >
              {subject}
            </span>
          ))}
        </div>
      </section>
    </div>
  );
}
